/**
 * Ported from sara/app `src/lib/hooks/use-element-width.ts` @ e8e8fecb4ec5.
 *
 * Bindery is canonical for this module from 2026-09-04; reconcile app-side
 * drift toward this copy. Deliberate divergence from the source: the hook
 * takes the element itself, not a `RefObject`. A ref object has one stable
 * identity for the component's life, so `subscribe` captured `ref.current`
 * exactly once at mount — an element rendered conditionally (or swapped)
 * arrived after that capture and was never observed, leaving the width `0`
 * forever. An element held in state changes `subscribe`'s identity when it
 * arrives, which is what makes `useSyncExternalStore` re-subscribe (the same
 * shape `useAudioPlayback` uses).
 */

import { useCallback, useSyncExternalStore } from "react";

const noop = () => undefined;

/**
 * Hook that reports the current `clientWidth` of a DOM element by
 * subscribing to its `ResizeObserver` via `useSyncExternalStore`. Per USES
 * boundary policy §2.3 the source of truth is the element's measured layout
 * width, owned by the browser — not React state.
 *
 * Pass the element via state and a callback ref, so late-mounting elements
 * re-subscribe:
 *
 * ```tsx
 * const [el, setEl] = useState<HTMLDivElement | null>(null);
 * const width = useElementWidth(el);
 * return <div ref={setEl}>…</div>;
 * ```
 *
 * SSR default: `0`.
 */
export function useElementWidth(element: HTMLElement | null): number {
  const subscribe = useCallback(
    (callback: () => void) => {
      if (!element || typeof ResizeObserver === "undefined") return noop;
      const observer = new ResizeObserver(() => callback());
      observer.observe(element);
      return () => observer.disconnect();
    },
    [element],
  );

  const getSnapshot = useCallback(() => element?.clientWidth ?? 0, [element]);

  return useSyncExternalStore(subscribe, getSnapshot, () => 0);
}
